import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskedLine, Reveal } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import Newsletter from "@/components/Newsletter";

const OUTFITS = [
  {
    id: "01",
    name: "The Classic",
    note: "The foundation. Heavy black tee, DR chest mark, cross artwork on the back. Everything else in the outfit builds on this.",
    pieces: ["CLASSIC DEJON RICHH TEE — $25", "BLACK DENIM", "WHITE CREW SOCKS", "LOW-PROFILE SNEAKERS"],
    img: "/products/pair.png",
    flip: false,
    live: true,
  },
  {
    id: "02",
    name: "The Statement",
    note: "Back artwork forward. Let the cross print do the talking — keep the rest of the fit quiet and monochrome.",
    pieces: ["CLASSIC DEJON RICHH TEE — $25", "CHARCOAL CARGOS", "BLACK CAP", "CHAIN-STITCH DETAIL"],
    img: "/products/back.png",
    flip: true,
    live: true,
  },
  {
    id: "03",
    name: "The Detail",
    note: "Close-up energy. The DR mark reads like a signature — subtle from a distance, unmistakable up close.",
    pieces: ["CLASSIC DEJON RICHH TEE — $25", "WASHED BLACK JEANS", "SILVER RING", "CANVAS TOTE"],
    img: "/products/detail.png",
    flip: false,
    live: true,
  },
  {
    id: "04",
    name: "Next Drop",
    note: "DR1 Graffiti. Raw sketchwork on premium blanks. Still in the studio — the list sees it first.",
    pieces: ["DR1 GRAFFITI TEE — COMING SOON", "MORE PIECES IN THE WORKS"],
    img: "/products/hero-front.png",
    flip: true,
    live: false,
  },
];

function OutfitSection({ outfit }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [40, -40]);

  return (
    <section ref={ref} className="border-b border-wire noise-overlay" data-testid={`outfit-${outfit.id}`}>
      <div className={`mx-auto grid max-w-[1600px] grid-cols-1 lg:grid-cols-12`}>
        <div className={`relative overflow-hidden lg:col-span-7 ${outfit.flip ? "lg:order-2" : ""}`}>
          <motion.div style={{ y }} className="p-6 sm:p-12">
            <div className="relative overflow-hidden border border-wire bg-charcoal">
              <img src={outfit.img} alt={`${outfit.name} outfit`} className="w-full object-cover" />
              <span className="absolute left-4 top-4 font-display text-5xl sm:text-7xl font-extrabold text-outline" style={{ WebkitTextStroke: "1px #444" }}>
                {outfit.id}
              </span>
            </div>
          </motion.div>
        </div>
        <div className={`flex flex-col justify-center px-6 py-14 sm:px-12 lg:col-span-5 lg:py-24 ${outfit.flip ? "lg:order-1 lg:border-r" : "lg:border-l"} border-wire`}>
          <Reveal>
            <p className="font-mono text-[10px] tracking-[0.35em] text-neutral-500">OUTFIT {outfit.id}</p>
            <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold uppercase tracking-tight text-white">
              {outfit.name}
            </h2>
            <p className="mt-6 max-w-md font-serif text-lg italic leading-relaxed text-neutral-300">{outfit.note}</p>
            <ul className="mt-8 space-y-2">
              {outfit.pieces.map((p) => (
                <li key={p} className="font-mono text-[11px] tracking-[0.25em] text-neutral-400">
                  <span className="text-neutral-600">/&nbsp;&nbsp;</span>{p}
                </li>
              ))}
            </ul>
            {outfit.live ? (
              <Link to="/product/classic-dejon-richh-tee" data-testid={`outfit-shop-${outfit.id}`}
                className="group mt-10 inline-flex w-fit items-center gap-3 bg-white px-10 py-4 font-mono text-[11px] tracking-[0.35em] text-black transition-colors hover:bg-neutral-300">
                SHOP THE OUTFIT <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
              </Link>
            ) : (
              <p className="mt-10 inline-block w-fit border border-wire px-8 py-4 font-mono text-[10px] tracking-[0.35em] text-neutral-400" data-testid={`outfit-locked-${outfit.id}`}>
                COMING SOON — JOIN THE LIST
              </p>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export default function Lookbook() {
  return (
    <div data-testid="lookbook-page">
      <section className="border-b border-wire pt-32 pb-16 sm:pt-44 sm:pb-24 noise-overlay">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8">
          <p className="font-mono text-[10px] tracking-[0.4em] text-neutral-500">FW26 — STYLED IN THE STUDIO</p>
          <h1 className="mt-6 font-display font-extrabold uppercase leading-[0.95] tracking-tight text-white">
            <MaskedLine delay={0.15} className="text-4xl sm:text-5xl lg:text-6xl">The Lookbook</MaskedLine>
            <MaskedLine delay={0.3} className="text-4xl sm:text-5xl lg:text-6xl">
              <span className="text-neutral-500">Complete outfits. Nothing extra.</span>
            </MaskedLine>
          </h1>
        </div>
      </section>

      {OUTFITS.map((o) => <OutfitSection key={o.id} outfit={o} />)}

      <Marquee items={["THE OUTFIT", "THE DETAILS", "THE ENERGY", "DEJON RICHH"]} />
      <Newsletter />
    </div>
  );
}
